import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';

/* The house cup: this day's RANKING turned into house points.

   ★★ Why this is not the houses screen next door. There a house's total is the plain sum of its
   teams' totals — right on a Hercules day, where every house fields the same number of teams, and
   wrong on a company day, where a house with four teams leads a house with two before anybody has
   played. The cup scores PLACEMENT, and it lands on PEOPLE: a team's points are split over the
   players who won them and each carries their share to their own house. Measured on live, every
   one of that day's eight teams was MIXED — so a team has no single house to give points to, and
   the split is not a refinement but the only honest reading.

   ★ The button is the feature. A standing that recomputes itself when somebody corrects a score
   three weeks later is not a result. Converting freezes it; converting again REPLACES it, so a
   corrected score goes through the mill a second time without doubling — and until then the screen
   says out loud that what is stored no longer matches what is played.

   ⚠️ A player in no house still holds a seat, so their share is earned and reaches nobody — it is
   NOT handed to their team-mates, which would credit people who did not earn it and hide that
   somebody is unplaced. Both the heads and the points it costs are drawn where an admin cannot
   miss them, because the fix is one screen away. */

const CompanyEventHouseCup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [scale, setScale] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/house-cup`)
      .then((res) => { setData(res); setScale(res.scale); })
      .catch(() => navigate('/programmes'));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const convert = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/house-cup`)
      .then(() => { setNotice(t('cupConverted')); load(); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const undo = () => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('cupUndoConfirm'))) return;
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/event/${eventId}/house-cup`)
      .then(() => { setNotice(t('cupUndone')); load(); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const saveScale = () => {
    if (busy || !scale) return;
    setBusy(true);
    setNotice(null);
    apiRequest('PUT', `/company/event/${eventId}/house-cup/scale`, { scale })
      .then((res) => {
        setScale(res.scale);
        setData((d) => ({ ...d, scale: res.scale, preview: res.preview }));
        setNotice(t('cupScaleSaved'));
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return <Base activeNav="home"><Card><p className="ct-field__hint">{t('pleaseWait')}</p></Card></Base>;
  }

  const preview = data.preview || {};
  const teams = preview.teams || [];
  const standing = preview.houses || [];
  const committed = data.committed || [];
  const canManage = !!data.can_manage;

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/houses`} />
        <Title my="1" className="mt-3">
          <Icon name="trophy" theme="primary" className="me-2" />
          {t('cupTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('cupNote')}</p>

        {/* ★ Said before anything else, because it is the one thing that makes every number below
            wrong: a team in no house carries its points nowhere. */}
        {preview.unplaced_people > 0 && (
          <p className="bravos-cal__notice">
            {t('cupUnplacedPeople', { count: preview.unplaced_people,
                                      points: preview.unplaced_points })}
            {' '}
            <button type="button" className="gl-link"
                    onClick={() => navigate('/company-members')}>
              {t('cupPutThemInAHouse')}
            </button>
          </p>
        )}
        {preview.scored_teams === 0 && (
          <p className="ce-note">{t('cupNothingScored')}</p>
        )}

        {/* WHAT IS STORED — first, because it is the answer to "did we already do this". */}
        {committed.length > 0 && (
          <>
            <p className="ct-pick__label">{t('cupCommitted')}</p>
            <div className="hv-counts">
              {committed.map((h) => (
                <div key={h.house_id} className="hv-count">
                  <span className="hv-count__n" style={h.color ? { color: h.color } : undefined}>
                    {h.points}
                  </span>
                  <span className="hv-count__label">{h.name}</span>
                </div>
              ))}
            </div>
            {/* ⚠️ The difference between stored and playable is the whole reason to press the
                button a second time, so the screen states it rather than leaving two lists to be
                compared by eye. */}
            {data.stale && <p className="bravos-cal__notice">{t('cupStale')}</p>}
          </>
        )}

        {/* WHAT CONVERTING WOULD WRITE. */}
        <p className="ct-pick__label mt-3">
          {committed.length > 0 ? t('cupWouldBecome') : t('cupStanding')}
        </p>
        {standing.length === 0 ? (
          <p className="ce-note">{t('cupNoHouses')}</p>
        ) : (
          <div className="cst-breakdown">
            {standing.map((h) => (
              <div key={h.house_id} className="cst-line">
                <span className="cst-line__label">
                  <span className="cst-line__swatch"
                        style={{ backgroundColor: h.color || '#5a6472' }} />
                  {h.rank}. {h.name}
                </span>
                <span className="cst-line__sub">
                  {t('statsNMembers', { count: h.players })}
                  {' · '}
                  {t('statsNTeams', { count: h.teams })}
                </span>
                <span className="cst-line__n">{h.points}</span>
              </div>
            ))}
          </div>
        )}

        {canManage && standing.length > 0 && (
          <div className="bravos-form__row mt-2">
            <button type="button" className="bravos-submit" onClick={convert} disabled={busy}>
              {busy ? t('pleaseWait')
                    : (committed.length > 0 ? t('cupConvertAgain') : t('cupConvert'))}
            </button>
            {committed.length > 0 && (
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={undo} disabled={busy}>
                {t('cupUndo')}
              </button>
            )}
          </div>
        )}
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {/* THE RANKING it comes from — the evidence for the standing above, in the order it was
          played out. A shared place is printed as a shared place (1, 1, 3), which is also how the
          points are handed out. */}
      <Card>
        <Title my="1">{t('cupRanking')}</Title>
        {teams.length === 0 ? (
          <p className="ce-note">{t('cupNoTeams')}</p>
        ) : (
          <div className="cst-breakdown">
            {teams.map((team) => (
              <div key={team.team_id} className="cst-line">
                <span className="cst-line__label">
                  {team.rank}. {team.name}
                  {/* Where this team's points landed. The split IS the model — without it a
                      house total is a number an admin has no way to check. */}
                  {(team.split || []).length > 0 && (
                    <span className="etags">
                      {team.split.map((s2) => (
                        <span key={s2.house_id} className="etag"
                              style={s2.color ? { borderColor: s2.color, color: s2.color }
                                              : undefined}>
                          {s2.name} {s2.points}
                        </span>
                      ))}
                    </span>
                  )}
                </span>
                <span className="cst-line__sub">
                  {t('cupRawTotal', { n: team.total })}
                </span>
                <span className="cst-line__n">{team.points}</span>
              </div>
            ))}
          </div>
        )}
        <p className="ce-note">{t('cupRankingNote')}</p>
        {/* ★ The two totals side by side. A standing nobody can add up is a standing nobody
            trusts, and the gap between these two is exactly the unplaced people above. */}
        <p className="ce-note">
          {t('cupReconcile', { handed: (teams || []).reduce((a, b) => a + (b.points || 0), 0),
                               landed: preview.awarded || 0 })}
        </p>
      </Card>

      {/* WHAT A PLACE IS WORTH — last, because it is set once and then almost never touched. */}
      {canManage && (
        <Card>
          <Title my="1">{t('cupScale')}</Title>
          <p className="bravos-q__hint mb-2">{t('cupScaleNote')}</p>
          <div className="cst-tiles">
            {(scale || []).map((n, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <label key={i} className="cst-tile">
                <span className="cst-tile__label">{t('cupPlace', { n: i + 1 })}</span>
                <input
                  type="number"
                  min="0"
                  value={n}
                  onChange={(e) => setScale(scale.map(
                    (v, j) => (j === i ? Number(e.target.value || 0) : v)))}
                />
              </label>
            ))}
          </div>
          <div className="bravos-form__row mt-2">
            <button type="button" className="bravos-submit" onClick={saveScale} disabled={busy}>
              {busy ? t('pleaseWait') : t('save')}
            </button>
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => setScale([...(scale || []), 0])}>
              <Icon name="plus" /> {t('cupAddPlace')}
            </button>
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => setScale(data.default_scale)}>
              {t('cupResetScale')}
            </button>
          </div>
          {/* ⚠️ An admin who changes the scale will expect the old days to follow. They do not,
              deliberately — that is a decision per day, not something a setting does behind their
              back — so the screen says so where the change is made. */}
          <p className="ce-note">{t('cupScaleNoRewrite')}</p>
        </Card>
      )}
    </Base>
  );
};

export default CompanyEventHouseCup;
